import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { AreaTrendChart, HorizontalMeters } from '../components/analytics/CommandCharts';
import api from '../api';
import {
  buildNotificationLevelRows,
  buildNotificationTrendRows,
  formatNotificationLevel,
} from '../utils/supportSignals';

export default function Notifications() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/admin/platform/notifications');
      setNotifications(data.notifications || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not load notification history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    const refresh = () => loadNotifications();
    window.addEventListener('admin-notifications:updated', refresh);
    return () => window.removeEventListener('admin-notifications:updated', refresh);
  }, [loadNotifications]);

  const filters = useMemo(
    () => ({
      view: searchParams.get('view') || '',
    }),
    [searchParams]
  );

  const visibleNotifications = useMemo(() => {
    return notifications.filter((notification) => {
      const recipient = String(notification?.metadata?.recipient || '').toLowerCase();
      const level = String(notification?.level || '').toLowerCase();

      if (filters.view === 'attention' && !['warning', 'critical'].includes(level)) return false;
      if (filters.view === 'broadcasts' && recipient !== 'all-users') return false;
      if (filters.view === 'direct' && recipient === 'all-users') return false;
      if (filters.view === 'unread' && notification?.readAt) return false;
      if (filters.view === 'actionable' && !notification?.actionUrl) return false;
      return true;
    });
  }, [filters.view, notifications]);

  const setView = (view) => {
    const next = new URLSearchParams(searchParams);
    if (view) next.set('view', view);
    else next.delete('view');
    setSearchParams(next);
  };

  const notificationLevelRows = useMemo(
    () =>
      buildNotificationLevelRows(notifications).map((row) => ({
        ...row,
        color:
          row.label === 'Critical'
            ? '#FF5D73'
            : row.label === 'Warning'
              ? '#FFCC00'
              : row.label === 'Success'
                ? '#7ED957'
                : '#67B7FF',
        accent:
          row.label === 'Critical'
            ? '#FF2446'
            : row.label === 'Warning'
              ? '#FF6D00'
              : row.label === 'Success'
                ? '#42B95C'
                : '#2F83FF',
      })),
    [notifications]
  );

  const notificationTrendRows = useMemo(
    () => buildNotificationTrendRows(notifications, 7),
    [notifications]
  );

  const summary = useMemo(() => {
    const attention = notifications.filter((item) =>
      ['warning', 'critical'].includes(String(item?.level || '').toLowerCase())
    ).length;
    const broadcasts = notifications.filter(
      (item) => String(item?.metadata?.recipient || '').toLowerCase() === 'all-users'
    ).length;
    const unread = notifications.filter((item) => !item?.readAt).length;
    const actionable = notifications.filter((item) => item?.actionUrl).length;

    return {
      total: notifications.length,
      attention,
      broadcasts,
      directs: notifications.length - broadcasts,
      unread,
      actionable,
    };
  }, [notifications]);

  const openAction = (notification) => {
    if (!notification?.actionUrl) return;
    if (notification.actionUrl.startsWith('/')) {
      navigate(notification.actionUrl);
      return;
    }
    window.open(notification.actionUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Layout searchPlaceholder="Search notification history...">
      <div className="space-y-8">
        <section className="rounded-[2.2rem] border border-[#f5b221]/18 bg-[radial-gradient(circle_at_top_left,rgba(245,178,33,0.22),transparent_36%),linear-gradient(180deg,#111111,#020202)] p-6 shadow-[0_24px_70px_rgba(0,0,0,0.4)] sm:p-8">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
            <div className="max-w-4xl">
              <p className="text-sm font-bold uppercase tracking-[0.28em] text-[#f7d88a]">
                Notifications
              </p>
              <h1 className="mt-3 text-3xl font-black tracking-[-0.03em] text-white sm:text-4xl">
                Review every customer-facing notification from one admin page.
              </h1>
              <p className="mt-3 max-w-4xl text-sm leading-7 text-[#cfcfcf]">
                This keeps warning traffic, broadcasts, direct sends, and follow-up links visible without burying them inside the broadcast composer.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={loadNotifications}
                className="rounded-full border border-[#f5b221]/28 px-5 py-3 text-sm font-bold text-[#f7d88a] transition hover:bg-[#f5b221]/10"
              >
                Refresh feed
              </button>
              <button
                type="button"
                onClick={() => navigate('/content')}
                className="rounded-full bg-[#f5b221] px-5 py-3 text-sm font-bold text-black transition hover:bg-[#f7d88a]"
              >
                Send broadcast
              </button>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-5">
          <MetricCard title="Recent total" value={summary.total} body="Latest notifications loaded into admin" />
          <MetricCard title="Needs attention" value={summary.attention} body="Warning or critical customer messages" />
          <MetricCard title="Broadcasts" value={summary.broadcasts} body="Sent to every Shimbirr user" />
          <MetricCard title="Direct sends" value={summary.directs} body="Targeted to one specific user" />
          <MetricCard title="Action links" value={summary.actionable} body="Notifications that include a follow-up path" />
        </section>

        {error ? (
          <div className="rounded-[1.8rem] border border-red-500/25 bg-red-950/15 px-5 py-4 text-sm text-red-200">
            {error}
          </div>
        ) : null}

        <section className="grid gap-6 xl:grid-cols-[1.1fr,0.9fr]">
          <div className="rounded-[2rem] border border-[#f5b221]/14 bg-[#070707] p-6 shadow-[0_18px_60px_rgba(0,0,0,0.34)]">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.24em] text-[#f7d88a]">
                  Notification Flow
                </p>
                <h2 className="mt-2 text-2xl font-black tracking-[-0.03em] text-white">
                  7-day notification activity
                </h2>
              </div>
              <p className="text-sm text-[#b8b8b8]">
                {summary.unread} unread and {summary.attention} elevated
              </p>
            </div>

            <div className="mt-6">
              <AreaTrendChart points={notificationTrendRows} color="#67B7FF" accent="#2F83FF" />
            </div>
          </div>

          <div className="rounded-[2rem] border border-[#f5b221]/14 bg-[#070707] p-6 shadow-[0_18px_60px_rgba(0,0,0,0.34)]">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.24em] text-[#f7d88a]">
                Severity Mix
              </p>
              <h2 className="mt-2 text-2xl font-black tracking-[-0.03em] text-white">
                Level breakdown
              </h2>
              <p className="mt-3 text-sm text-[#b8b8b8]">
                See whether your outbound notification pressure is mostly informational or escalated.
              </p>
            </div>

            <div className="mt-6">
              {notificationLevelRows.length ? (
                <HorizontalMeters items={notificationLevelRows} />
              ) : (
                <EmptyPanel message="No notifications have been sent yet." />
              )}
            </div>
          </div>
        </section>

        <section className="rounded-[2rem] border border-[#f5b221]/14 bg-[#070707] p-6 shadow-[0_18px_60px_rgba(0,0,0,0.34)]">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-2xl font-black tracking-[-0.03em] text-white">
                Notification feed
              </h2>
              <p className="mt-2 text-sm text-[#b8b8b8]">
                Filter the latest admin-triggered notifications and jump into the related action when a link is attached.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <FilterChip active={!filters.view} label="All" onClick={() => setView('')} />
              <FilterChip
                active={filters.view === 'attention'}
                label="Attention"
                onClick={() => setView(filters.view === 'attention' ? '' : 'attention')}
              />
              <FilterChip
                active={filters.view === 'broadcasts'}
                label="Broadcasts"
                onClick={() => setView(filters.view === 'broadcasts' ? '' : 'broadcasts')}
              />
              <FilterChip
                active={filters.view === 'direct'}
                label="Direct"
                onClick={() => setView(filters.view === 'direct' ? '' : 'direct')}
              />
              <FilterChip
                active={filters.view === 'actionable'}
                label="Action links"
                onClick={() => setView(filters.view === 'actionable' ? '' : 'actionable')}
              />
            </div>
          </div>

          {loading ? (
            <div className="mt-6 rounded-[1.6rem] border border-dashed border-[#2b2b2b] bg-black/40 p-5 text-sm text-[#989898]">
              Loading notification history...
            </div>
          ) : (
            <div className="mt-6 space-y-4">
              {!visibleNotifications.length ? (
                <EmptyPanel
                  message={
                    filters.view
                      ? 'No notifications match the current filter.'
                      : 'No notification history is available yet.'
                  }
                />
              ) : (
                visibleNotifications.map((notification) => {
                  const recipient = notification?.metadata?.recipient || 'all-users';

                  return (
                    <div key={notification._id} className="rounded-[1.8rem] border border-[#f5b221]/10 bg-black/60 p-5">
                      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                        <div className="max-w-4xl">
                          <div className="flex flex-wrap items-center gap-2">
                            <LevelPill level={notification.level} />
                            {!notification?.readAt ? (
                              <span className="rounded-full border border-[#4e4632] bg-[#111111] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-[#f7d88a]">
                                Unread
                              </span>
                            ) : null}
                          </div>
                          <h3 className="mt-3 text-lg font-bold text-white">
                            {notification.title || 'Untitled notification'}
                          </h3>
                          <p className="mt-3 text-sm leading-7 text-[#c9c9c9]">
                            {notification.body || 'No body provided.'}
                          </p>
                        </div>

                        {notification?.actionUrl ? (
                          <button
                            type="button"
                            onClick={() => openAction(notification)}
                            className="rounded-full border border-[#f5b221]/28 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-[#f7d88a] transition hover:bg-[#f5b221]/10"
                          >
                            {notification.actionLabel || 'Open action'}
                          </button>
                        ) : null}
                      </div>

                      <div className="mt-4 grid gap-3 text-sm text-[#b6b6b6] sm:grid-cols-2 xl:grid-cols-5">
                        <InfoField label="Recipient" value={recipient} />
                        <InfoField label="Type" value={notification.type || 'admin_broadcast'} />
                        <InfoField label="Created" value={formatDateTime(notification.createdAt)} />
                        <InfoField label="Read" value={notification.readAt ? formatDateTime(notification.readAt) : 'Not yet'} />
                        <InfoField label="Action" value={notification.actionUrl || 'No action link'} />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}

function MetricCard({ title, value, body }) {
  return (
    <div className="rounded-[1.8rem] border border-[#f5b221]/12 bg-[linear-gradient(180deg,#111111,#040404)] p-5">
      <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8f8f8f]">{title}</p>
      <p className="mt-3 text-4xl font-black tracking-[-0.04em] text-[#f5b221]">{value}</p>
      <p className="mt-2 text-sm text-[#b4b4b4]">{body}</p>
    </div>
  );
}

function FilterChip({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] transition',
        active
          ? 'bg-[#f5b221] text-black'
          : 'border border-[#4e4632] text-[#d2c5ab] hover:bg-[#111111]',
      ].join(' ')}
    >
      {label}
    </button>
  );
}

function LevelPill({ level }) {
  const normalized = String(level || '').toLowerCase();
  const tone =
    normalized === 'critical'
      ? 'border-red-500/30 bg-red-500/10 text-red-100'
      : normalized === 'warning'
        ? 'border-[#fd6c00]/35 bg-[#fd6c00]/10 text-[#ffcfb0]'
        : normalized === 'success'
          ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-100'
          : 'border-sky-500/30 bg-sky-500/10 text-sky-100';

  return (
    <span className={`rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] ${tone}`}>
      {formatNotificationLevel(level)}
    </span>
  );
}

function InfoField({ label, value }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#7f7f7f]">{label}</p>
      <p className="mt-1 break-all text-white">{value}</p>
    </div>
  );
}

function EmptyPanel({ message }) {
  return (
    <div className="rounded-[1.6rem] border border-dashed border-[#2b2b2b] bg-black/40 p-5 text-sm text-[#989898]">
      {message}
    </div>
  );
}

function formatDateTime(value) {
  if (!value) return 'Unknown time';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}
